// USPAHomePage.js — Inicio ACHIPO (rediseño estilo federación)
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import API_CONFIG from '../config/api';
import { getHomeImagesFromAPI } from '../services/cmsService';
import { getAsociadosPublic } from '../services/clubsService';
import { formatFechaLarga, partesFecha } from '../utils/fechas';
import { getCachedImage } from '../utils/cmsCache';
import { getAccessToken } from '../services/api';
import MarcasPendientesPopup from './MarcasPendientesPopup';
import '../App.css';
import '../styles/home/HomePage.css';

const PUBLIC = process.env.PUBLIC_URL || '';
const apiBase = API_CONFIG.BASE_URL;

const LINK_DENUNCIA =
  'https://docs.google.com/forms/u/3/d/1x9dAmsaJ60_NPCqxDVs2D0XiY5mkJI_Ss-OXOOgIe0Y/edit?usp=drive_web';
const LINK_LIFTINGCAST = 'https://liftingcast.com/meets/mle6s8hzkh35/results';

// Pasos de inicio. La imagen n se edita en el CMS como "carousel_n".
const PASOS = [
  {
    titulo: 'Aprende lo básico',
    texto: 'Reglamentos IPL y WRPF, divisiones y equipamiento permitido.',
    cta: 'Reglamentos',
    to: '/rules',
    fallback: `${PUBLIC}/IMG_8954.JPG`,
    alt: 'Atletas compartiendo tras un levantamiento',
  },
  {
    titulo: 'Encuentra un evento',
    texto: 'Revisa las próximas competencias y elige tu primera plataforma.',
    cta: 'Ver eventos',
    to: '/events',
    fallback: `${PUBLIC}/IMG_3165B9EABF43-11.jpeg`,
    alt: 'Atleta en peso muerto',
  },
  {
    titulo: 'Únete a ACHIPO',
    texto: 'Afíliate para inscribirte en competencias y aparecer en el ranking.',
    cta: 'Hazte miembro',
    afiliacion: true,
    fallback: `${PUBLIC}/IMG_3165B9EABF43-1.jpeg`,
    alt: 'Equipo de atletas en el Nacional ACHIPO',
  },
  {
    titulo: 'Mide tu nivel',
    texto: 'Revisa tu clasificación IPL y calcula tus DOTS.',
    cta: 'Calcular DOTS',
    to: '/clasificacion',
    fallback: `${PUBLIC}/IMG_8955.JPG`,
    alt: 'Coach preparando a un atleta',
  },
];

// Partners nacionales (logos blancos con fondo transparente: van sobre fondo oscuro).
const PARTNERS_BASE = 'https://achipo-front-statics.s3.us-east-2.amazonaws.com/public/partners';
const PARTNERS = [
  { nombre: 'Berserker', logo: `${PARTNERS_BASE}/berserker.png` },
  { nombre: 'ILUS', logo: `${PARTNERS_BASE}/ilus.png` },
  { nombre: 'Strongest Supplements', logo: `${PARTNERS_BASE}/strongest.png` },
];

const REQUERIDO = ['Afiliación ACHIPO vigente', 'Singlet aprobado', 'Equipamiento aprobado', 'Cédula de identidad', 'Inscripción al evento'];
const UTIL = ['Coach o handler', 'Club asociado', 'Primeros intentos definidos', 'Comida para el día', 'Tu clasificación y DOTS'];

const Check = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#E8372B" strokeWidth="3.5" aria-hidden="true">
    <path d="M4 12l5 5L20 6" />
  </svg>
);

const enlaceInscripcion = (e) => {
  const tipo = String(e.formulario_inscripcion_tipo || '').trim().toLowerCase();
  const raw = String(e.formulario_inscripcion_url || '').trim();
  if (tipo === 'externo' && raw) {
    return { externo: true, href: /^https?:\/\//i.test(raw) ? raw : `https://${raw}` };
  }
  return { externo: false, to: `/events/${e.id}/formulario` };
};

const ubicacion = (e) => [e.ciudad, e.region].filter(Boolean).join(', ');

const formatNum = (v, dec = 0) =>
  v == null || v === '' || Number.isNaN(Number(v))
    ? '—'
    : Number(v).toLocaleString('es-CL', { minimumFractionDigits: dec, maximumFractionDigits: dec || 2 });

const nombreBonito = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/(^|\s|-)(\p{L})/gu, (m, sep, l) => sep + l.toUpperCase());

/* ─────────────────────────── Hero ─────────────────────────── */
const Hero = ({ imagen, onAfiliacion, anioRanking, totalClubes }) => (
  <section
    className="home-hero"
    style={{
      // imagen: undefined = consultando el CMS (solo fondo oscuro); null = sin imagen en CMS (respaldo local)
      backgroundImage: `linear-gradient(90deg, rgba(0,0,0,.92) 0%, rgba(0,0,0,.7) 45%, rgba(0,0,0,.25) 100%)${
        imagen === undefined ? '' : `, url(${imagen || `${PUBLIC}/IMG_8956.JPG`})`
      }`,
    }}
  >
    <div className="home-hero-inner">
      <p className="home-hero-pre">FORJADOS EN FUERZA</p>
      <h1 className="ach-display home-hero-title">
        Unidos en la
        <br />
        <span>plataforma</span>
      </h1>
      <p className="home-hero-sub">Cada levantamiento, una prueba de voluntad.</p>
      <div className="home-hero-ctas">
        <Link to="/events" className="ach-pill ach-pill--red">Ver eventos</Link>
        <Link to="/rankings" className="ach-pill ach-pill--light">Ranking {anioRanking || ''}</Link>
        <button type="button" className="ach-pill ach-pill--light" onClick={onAfiliacion}>Hazte miembro</button>
      </div>
      <ul className="home-hero-points">
        <li>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="M9 12l2 2 4-4" /></svg>
          <div><strong>TESTED Y OPEN</strong><span>Competencias IPL y WRPF</span></div>
        </li>
        <li>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="9" cy="7" r="4" /><path d="M17 11a3 3 0 1 0 0-6" /><path d="M1 21v-1a6 6 0 0 1 12 0v1" /><path d="M17 15a5 5 0 0 1 6 5v1" /></svg>
          <div><strong>{totalClubes ? `${totalClubes} CLUBES` : 'CLUBES EN TODO CHILE'}</strong><span>Asociados a lo largo del país</span></div>
        </li>
        <li>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M8 7l-5 5 5 5" /><path d="M16 7l5 5-5 5" /><path d="M3 12h18" /></svg>
          <div><strong>ABIERTO A TODOS</strong><span>Sub-junior a Master</span></div>
        </li>
      </ul>
    </div>
  </section>
);

/* ─────────────────────────── Pasos ─────────────────────────── */
const Pasos = ({ imagenes, onAfiliacion }) => (
  <section className="home-steps">
    <div className="home-steps-grid">
      {PASOS.map((p, i) => (
        <article key={p.titulo} className="home-step">
          {imagenes?.[i]?.image === undefined ? (
            <span className="home-step-img-placeholder" aria-hidden="true" />
          ) : (
            <img src={imagenes[i].image || p.fallback} alt={p.alt} loading="lazy" />
          )}
          <div className="home-step-body">
            <h3 className="ach-display">{p.titulo}</h3>
            <p>{p.texto}</p>
            {p.afiliacion ? (
              <button type="button" className="ach-pill ach-pill--dark" onClick={onAfiliacion}>{p.cta}</button>
            ) : (
              <Link to={p.to} className="ach-pill ach-pill--dark">{p.cta}</Link>
            )}
          </div>
        </article>
      ))}
    </div>
  </section>
);

/* ─────────────────────────── Eventos ─────────────────────────── */
const TarjetaDestacado = ({ evento, imagenFallback }) => {
  const insc = enlaceInscripcion(evento);
  const fecha = formatFechaLarga(evento.fecha_inicio);
  return (
    <article className="home-feat-card">
      <div className="home-feat-img">
        <img src={evento.imagen_url || imagenFallback || `${PUBLIC}/achipo.jpg`} alt={`Afiche ${evento.nombre}`} loading="lazy" />
      </div>
      <div className="home-feat-body">
        <h3 className="ach-display">{evento.nombre}</h3>
        <p>{[fecha, evento.ciudad].filter(Boolean).join(' · ').toUpperCase()}</p>
        <Link to={`/events?id=${encodeURIComponent(evento.id)}`} className="ach-pill ach-pill--dark ach-pill--sm">Detalles</Link>
        {insc.externo ? (
          <a href={insc.href} target="_blank" rel="noopener noreferrer" className="ach-pill ach-pill--grad ach-pill--sm">Inscribirme</a>
        ) : (
          <Link to={insc.to} className="ach-pill ach-pill--grad ach-pill--sm">Inscribirme</Link>
        )}
      </div>
    </article>
  );
};

const Eventos = ({ destacados, proximos, cargando, imagenesCms }) => {
  const [region, setRegion] = useState('');
  const pistaRef = useRef(null);

  const regiones = useMemo(
    () => [...new Set(proximos.map((e) => e.region).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'es')),
    [proximos]
  );
  const lista = region ? proximos.filter((e) => e.region === region) : proximos;

  const mover = (dir) => {
    const el = pistaRef.current;
    if (!el) return;
    const card = el.querySelector('.home-feat-card');
    const paso = card ? card.getBoundingClientRect().width + 16 : 300;
    el.scrollBy({ left: dir * paso, behavior: 'smooth' });
  };

  return (
    <section className="ach-section ach-section--dark">
      <div className="ach-container home-events-grid">
        <div>
          <div className="home-center">
            <span className="ach-eyebrow-outline home-eyebrow">Compite</span>
            <h2 className="ach-display ach-section-title">Eventos destacados</h2>
          </div>
          {cargando ? (
            <p className="home-muted home-center home-gap">Cargando eventos…</p>
          ) : destacados.length === 0 ? (
            <p className="home-muted home-center home-gap">No hay eventos destacados por ahora.</p>
          ) : (
            <>
              <div className="home-feat-track" ref={pistaRef}>
                {destacados.map((e, i) => (
                  <TarjetaDestacado key={e.id} evento={e} imagenFallback={imagenesCms?.[i]?.image} />
                ))}
              </div>
              <div className="home-feat-controls">
                <div className="home-feat-arrows">
                  <button type="button" aria-label="Evento anterior" onClick={() => mover(-1)}>‹</button>
                  <button type="button" aria-label="Evento siguiente" onClick={() => mover(1)}>›</button>
                </div>
                <Link to="/events" className="ach-pill ach-pill--light">Ver todos</Link>
              </div>
            </>
          )}
        </div>

        <div>
          <div className="home-center">
            <span className="ach-eyebrow-outline home-eyebrow">Cerca de ti</span>
            <h2 className="ach-display ach-section-title">Próximos eventos</h2>
          </div>
          <label className="home-region">
            <span className="home-sr">Filtrar por región</span>
            <select value={region} onChange={(e) => setRegion(e.target.value)}>
              <option value="">REGIÓN — TODAS</option>
              {regiones.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </label>
          <div className="home-event-list">
            {cargando && <p className="home-muted">Cargando…</p>}
            {!cargando && lista.length === 0 && (
              <p className="home-muted">{region ? 'No hay eventos próximos en esta región.' : 'No hay eventos próximos publicados.'}</p>
            )}
            {lista.map((e) => {
              const f = partesFecha(e.fecha_inicio);
              return (
                <Link key={e.id} to={`/events?id=${encodeURIComponent(e.id)}`} className="home-event-row">
                  <span className="home-event-date">
                    <span className="ach-display">{f.dia}</span>
                    <span>{f.mes}</span>
                  </span>
                  <span>
                    <span className="ach-display home-event-name">{e.nombre}</span>
                    <span className="home-event-place">{ubicacion(e) || 'Lugar por confirmar'}</span>
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────── Ranking ─────────────────────────── */
const ListaRanking = ({ titulo, grupo }) => {
  const atletas = grupo?.atletas || [];
  return (
    <div className="home-rank-card">
      <div className="home-rank-head">
        <h3 className="ach-display">{titulo}</h3>
        <span>{atletas.length ? `${atletas.length} atletas · por DOTS` : 'Por DOTS'}</span>
      </div>
      {atletas.length === 0 ? (
        <p className="home-muted">Sin atletas en esta categoría.</p>
      ) : (
        <ol className="home-rank-list">
          {atletas.map((a) => {
            const medalla = a.puesto <= 3 ? `ach-medal-${a.puesto} is-medal` : '';
            return (
              <li key={`${a.clave_atleta || a.nombre}-${a.puesto}`} className={`home-rank-row ${medalla}`}>
                <span className="ach-display home-rank-pos">{a.puesto}</span>
                <span className="home-rank-name">
                  <strong>{nombreBonito(a.nombre)}</strong>
                  <small>
                    Total {formatNum(a.total, Number(a.total) % 1 ? 1 : 0)} kg · BW {formatNum(a.peso_corporal, 2)}
                  </small>
                </span>
                <span className="home-rank-dots">
                  <span className="ach-display">{formatNum(a.dots, 2)}</span>
                  <small>DOTS</small>
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

const RankingGeneral = ({ ranking, cargando }) => {
  const [modalidad, setModalidad] = useState('raw');
  const [tipo, setTipo] = useState('tested');
  const grupos = useMemo(() => ranking?.grupos || [], [ranking]);

  const modalidades = useMemo(() => {
    const set = [...new Set(grupos.map((g) => g.modalidad).filter(Boolean))];
    return set.length ? set : ['raw', 'classic'];
  }, [grupos]);

  const buscar = (genero) =>
    grupos.find((g) => g.modalidad === modalidad && g.tipo_competencia === tipo && g.genero === genero);

  const etiquetaModalidad = (m) => (m === 'classic' || m === 'classic_raw' ? 'Classic' : m === 'raw' ? 'Raw' : nombreBonito(m));

  return (
    <section className="ach-section ach-section--black home-rank">
      <div className="ach-container">
        <div className="home-rank-top">
          <div>
            <span className="ach-eyebrow-outline home-eyebrow">Ranking general</span>
            <h2 className="ach-display ach-section-title">
              Los más fuertes
              <br />
              de Chile{ranking?.anio ? ` · ${ranking.anio}` : ''}
            </h2>
          </div>
          <div className="home-tabs">
            <div role="group" aria-label="Modalidad">
              {modalidades.map((m) => (
                <button key={m} type="button" className={modalidad === m ? 'on' : ''} aria-pressed={modalidad === m} onClick={() => setModalidad(m)}>
                  {etiquetaModalidad(m)}
                </button>
              ))}
            </div>
            <div role="group" aria-label="Tipo de competencia">
              {['tested', 'untested'].map((t) => (
                <button key={t} type="button" className={tipo === t ? 'on' : ''} aria-pressed={tipo === t} onClick={() => setTipo(t)}>
                  {t === 'tested' ? 'Tested' : 'Untested'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {cargando ? (
          <p className="home-muted home-gap">Cargando ranking…</p>
        ) : grupos.length === 0 ? (
          <p className="home-muted home-gap">Todavía no hay un ranking publicado.</p>
        ) : (
          <div className="home-rank-grid">
            <ListaRanking titulo="Masculino" grupo={buscar('Masculino')} />
            <ListaRanking titulo="Femenino" grupo={buscar('Femenino')} />
          </div>
        )}

        <div className="home-rank-ctas">
          <Link to="/rankings" className="ach-pill ach-pill--light">Ranking por categoría</Link>
          <Link to="/clasificacion" className="ach-pill ach-pill--red">Calcula tus DOTS</Link>
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────── Primera competencia ─────────────────────────── */
const RECURSOS = [
  {
    titulo: 'Reglamentos',
    texto: 'IPL, técnico WRPF y estándares de clasificación',
    to: '/rules',
    icono: <path d="M4 4h12a4 4 0 0 1 4 4v12H8a4 4 0 0 1-4-4zM8 8h8M8 12h8" />,
  },
  {
    titulo: 'LiftingCast nacional',
    texto: 'Resultados y estadísticas de las competencias',
    href: LINK_LIFTINGCAST,
    icono: <path d="M3 3v18h18M7 15l4-4 3 3 5-6" />,
  },
  {
    titulo: 'Denuncia, ética y disciplina',
    texto: 'Formulario para denuncias y consultas',
    href: LINK_DENUNCIA,
    icono: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10zM12 8v4M12 16h.01" />,
  },
];

const PrimeraCompetencia = () => (
  <section className="ach-section ach-section--white">
    <div className="ach-container home-first-grid">
      <div>
        <span className="ach-eyebrow-outline home-eyebrow" style={{ color: '#111' }}>Lo que necesitas</span>
        <h2 className="ach-display ach-section-title" style={{ color: '#111' }}>
          Antes de tu
          <br />
          primera competencia
        </h2>
        <div className="home-checklists">
          <div>
            <h3 className="ach-display">Requerido</h3>
            <ul>{REQUERIDO.map((t) => <li key={t}><Check />{t}</li>)}</ul>
          </div>
          <div>
            <h3 className="ach-display">Útil</h3>
            <ul>{UTIL.map((t) => <li key={t}><Check />{t}</li>)}</ul>
          </div>
        </div>
      </div>
      <div className="home-resources">
        {RECURSOS.map((r) => {
          const contenido = (
            <>
              <span className="home-resource-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{r.icono}</svg>
              </span>
              <span className="home-resource-text">
                <span className="ach-display">{r.titulo}</span>
                <span>{r.texto}</span>
              </span>
              <span aria-hidden="true" className="home-resource-arrow">→</span>
            </>
          );
          return r.to ? (
            <Link key={r.titulo} to={r.to} className="home-resource">{contenido}</Link>
          ) : (
            <a key={r.titulo} href={r.href} target="_blank" rel="noopener noreferrer" className="home-resource">{contenido}</a>
          );
        })}
      </div>
    </div>
  </section>
);

/* ─────────────────────────── Partners ─────────────────────────── */
const Partners = () => (
  <section className="ach-section ach-section--black home-partners" aria-labelledby="home-partners-title">
    <div className="ach-container">
      <h2 id="home-partners-title" className="ach-display ach-section-title" style={{ margin: 0 }}>
        ACHIPO National Partners
      </h2>
      <ul className="home-partners-grid">
        {PARTNERS.map((p) => (
          <li key={p.nombre} className="home-partner">
            {p.url ? (
              <a href={p.url} target="_blank" rel="noopener noreferrer" aria-label={p.nombre}>
                <img src={p.logo} alt={p.nombre} loading="lazy" />
              </a>
            ) : (
              <img src={p.logo} alt={p.nombre} loading="lazy" />
            )}
          </li>
        ))}
      </ul>
    </div>
  </section>
);

/* ─────────────────────────── Clubes ─────────────────────────── */
const Clubes = ({ clubes }) => {
  if (!clubes.length) return null;
  return (
    <section className="ach-section ach-section--dark">
      <div className="ach-container">
        <div className="home-clubs-head">
          <h2 className="ach-display ach-section-title" style={{ margin: 0 }}>Clubes asociados</h2>
          <Link to="/associates" className="ach-pill ach-pill--light">Ver asociados</Link>
        </div>
        <div className="home-clubs-grid">
          {clubes.map((c) => (
            <div key={c.id} className="home-club">
              {c.logo_url ? (
                <img src={c.logo_url} alt={`Logo ${c.nombre}`} loading="lazy" />
              ) : (
                <span className="home-club-initial ach-display" aria-hidden="true">{String(c.nombre || '?').charAt(0)}</span>
              )}
              <span>
                <span className="ach-display home-club-name">{nombreBonito(c.nombre)}</span>
                <span className="home-club-meta">
                  {[c.ciudad, c.miembros != null ? `${c.miembros} miembros` : null].filter(Boolean).join(' · ')}
                </span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────── Colección ─────────────────────────── */
const Coleccion = ({ imagen }) => (
  <section
    className="home-collection"
    style={{
      backgroundImage: `linear-gradient(90deg, rgba(0,0,0,.85) 0%, rgba(0,0,0,.55) 55%, rgba(0,0,0,.2) 100%)${imagen ? `, url(${imagen})` : ''}`,
    }}
  >
    <div className="ach-container home-collection-inner">
      <div>
        <span className="ach-eyebrow-outline home-eyebrow">Tienda oficial</span>
        <h2 className="ach-display home-collection-title">
          ACHIPO
          <br />
          colección ícono
        </h2>
        <Link to="/shop" className="ach-pill ach-pill--light">Revisar colección</Link>
      </div>
      <div className="home-collection-imgs">
        <Link to="/shop#product-2"><img src={`${PUBLIC}/IMG_BFE796AFF051-1.jpeg`} alt="Polerón ACHIPO" loading="lazy" /></Link>
        <Link to="/shop#product-3"><img src={`${PUBLIC}/IMG_8E42A8A33857-2.jpeg`} alt="Cinturón de levantamiento" loading="lazy" /></Link>
      </div>
    </div>
  </section>
);

/* ─────────────────────────── Página ─────────────────────────── */
const USPAHomePage = () => {
  const navigate = useNavigate();
  // Imágenes del CMS: se parte con las últimas conocidas (caché del navegador) para no mostrar
  // primero una imagen de respaldo que luego cambia. undefined = aún no se sabe.
  const [imagenes, setImagenes] = useState(() => ({
    hero_bg: getCachedImage('hero_bg'),
    banner_bg: getCachedImage('banner_bg'),
    carousel: [1, 2, 3, 4, 5].map((id) => ({ id, image: getCachedImage(`carousel_${id}`) })),
    events: [1, 2, 3, 4].map((id) => ({ id, image: getCachedImage(`event_${id}`) })),
  }));
  // Popup de nuevas marcas: solo si hay sesión activa (mismo popup que en Mi perfil)
  const [sesionActiva, setSesionActiva] = useState(
    () => !!getAccessToken() && localStorage.getItem('isLoggedIn') === 'true'
  );
  useEffect(() => {
    const alSalir = () => setSesionActiva(false);
    window.addEventListener('userLogout', alSalir);
    return () => window.removeEventListener('userLogout', alSalir);
  }, []);
  const [destacados, setDestacados] = useState([]);
  const [proximos, setProximos] = useState([]);
  const [cargandoEventos, setCargandoEventos] = useState(true);
  const [ranking, setRanking] = useState(null);
  const [cargandoRanking, setCargandoRanking] = useState(true);
  const [clubes, setClubes] = useState([]);

  const cargarImagenes = useCallback(async () => {
    try {
      const data = await getHomeImagesFromAPI();
      const porId = (lista, id) => (lista || []).find((x) => x && x.id === id)?.image || null;
      setImagenes({
        hero_bg: data?.hero_bg || null,
        banner_bg: data?.banner_bg || null,
        carousel: [1, 2, 3, 4, 5].map((id) => ({ id, image: porId(data?.carousel, id) })),
        events: [1, 2, 3, 4].map((id) => ({ id, image: porId(data?.events, id) })),
      });
    } catch {
      // Sin respuesta del CMS: se usan las imágenes locales de respaldo
      setImagenes((prev) => ({
        hero_bg: prev.hero_bg ?? null,
        banner_bg: prev.banner_bg ?? null,
        carousel: prev.carousel.map((c) => ({ ...c, image: c.image ?? null })),
        events: prev.events.map((e) => ({ ...e, image: e.image ?? null })),
      }));
    }
  }, []);

  useEffect(() => {
    cargarImagenes();
    const onFocus = () => cargarImagenes();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [cargarImagenes]);

  useEffect(() => {
    let cancelado = false;
    const json = (r) => (r.ok ? r.json() : []);
    Promise.all([
      fetch(`${apiBase}/api/eventos/destacados`).then(json).catch(() => []),
      fetch(`${apiBase}/api/eventos/landing`).then(json).catch(() => []),
    ]).then(([d, l]) => {
      if (cancelado) return;
      setDestacados(Array.isArray(d) ? d : []);
      setProximos(Array.isArray(l) ? l : []);
      setCargandoEventos(false);
    });

    fetch(`${apiBase}${API_CONFIG.ENDPOINTS.RANKINGS.PUBLICO}?tipo=overall`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelado) setRanking(data && data.success !== false ? data : null);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelado) setCargandoRanking(false);
      });

    getAsociadosPublic()
      .then((items) => {
        if (!cancelado) setClubes((items || []).filter((a) => a.tipo === 'club'));
      })
      .catch(() => {});

    return () => {
      cancelado = true;
    };
  }, []);

  const irAfiliacion = () => {
    const token = localStorage.getItem('access_token');
    navigate(token ? '/profile#afiliacion' : '/login');
  };

  return (
    <div className="main-container home-page">
      <Header />
      {sesionActiva && <MarcasPendientesPopup claveSesion="achipo_marcas_popup_inicio" />}
      <main className="home-main">
        <Hero imagen={imagenes.hero_bg} onAfiliacion={irAfiliacion} anioRanking={ranking?.anio} totalClubes={clubes.length} />
        <Pasos imagenes={imagenes.carousel} onAfiliacion={irAfiliacion} />
        <Eventos destacados={destacados} proximos={proximos} cargando={cargandoEventos} imagenesCms={imagenes.events} />
        <RankingGeneral ranking={ranking} cargando={cargandoRanking} />
        <PrimeraCompetencia />
        <section className="home-member-band">
          <div className="ach-container">
            <h2 className="ach-display">¿Aún no tienes tu afiliación ACHIPO?</h2>
            <button type="button" className="ach-pill ach-pill--red" onClick={irAfiliacion}>Hazte miembro</button>
          </div>
        </section>
        <Partners />
        <Clubes clubes={clubes} />
        <Coleccion imagen={imagenes.banner_bg} />
      </main>
      <Footer />
    </div>
  );
};

export default USPAHomePage;
